/**
 * A nudge that something was written.
 *
 * Screens load their data once and keep it in state. That is right for reads,
 * but it means a write made somewhere else on the page leaves them showing
 * what was true a moment ago. Logging a call from the assistant is exactly
 * that case: the entry is created, and the dashboard feed and the bell carry
 * on showing the list they fetched on mount.
 *
 * So writes announce themselves and anything displaying that data refetches.
 * It is what the screens will do against the API anyway, since a mutation
 * there also has to be followed by a read to see its effect. Swapping in a
 * websocket or a poll later means changing this file and nothing else.
 *
 * Deliberately not an event bus with topics. Everything refetches, which for
 * a few hundred rows held in the browser is cheaper than getting the
 * bookkeeping of who cares about what wrong.
 */

type Listener = () => void;

const listeners = new Set<Listener>();

/** Refetch when anything is written. Returns the unsubscribe. */
export function onDataChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Announce a write. Called by the data layer, not by screens. */
export function dataChanged(): void {
  for (const listener of listeners) listener();
}
